import { qualitySchema } from '@wyn/content';
import { z } from 'zod';
import { actionIdSchema, playerSnapshotSchema } from './schemas.ts';

const actionResponse = <O extends z.ZodType>(outcome: O) => z.object({ player: playerSnapshotSchema, outcome });
const coin = z.number().int().min(1).max(100_000_000);
const itemKey = z.string().min(1).max(60);

// ---- The Royal Bank (/v1/market/bank/*) ----

export const bankLockSchema = z.object({
  id: z.string(),
  amount: z.number().int(),
  days: z.number().int(),
  rate: z.number(),
  interest: z.number().int(),
  startedAt: z.number(),
  maturesAt: z.number(),
});
export type BankLockView = z.infer<typeof bankLockSchema>;

export const bankResponseSchema = z.object({
  purse: z.number().int(),
  bank: z.number().int(),
  locked: z.number().int(),
  locks: z.array(bankLockSchema),
  /** Fixed interest for a whole term, by days. */
  rates: z.array(z.object({ days: z.number().int(), rate: z.number() })),
});
export type BankResponse = z.infer<typeof bankResponseSchema>;

export const bankMoveRequestSchema = z.object({ actionId: actionIdSchema, amount: coin });
const bankOutcome = z.object({ amount: z.number().int(), purse: z.number().int(), bank: z.number().int() });
export const depositResponseSchema = actionResponse(bankOutcome);
export const withdrawResponseSchema = actionResponse(bankOutcome);
export const lockRequestSchema = z.object({ actionId: actionIdSchema, amount: coin, days: z.number().int() });
export const lockResponseSchema = actionResponse(bankLockSchema);

// ---- The order book (/v1/market/*) ----

export const orderSideSchema = z.enum(['buy', 'sell']);
export const marketOrderSchema = z.object({
  id: z.string(),
  side: orderSideSchema,
  itemKey: z.string(),
  quality: qualitySchema,
  price: z.number().int(),
  quantity: z.number().int(),
  remaining: z.number().int(),
  createdAt: z.number(),
});
export type MarketOrderView = z.infer<typeof marketOrderSchema>;

export const bookRequestSchema = z.object({ itemKey, quality: qualitySchema });
const level = z.object({ price: z.number().int(), quantity: z.number().int(), orders: z.number().int() });
/** Price levels, best first. Who placed each order is not shown. */
export const bookResponseSchema = z.object({ itemKey: z.string(), quality: qualitySchema, buys: z.array(level), sells: z.array(level) });
export type BookResponse = z.infer<typeof bookResponseSchema>;

export const ordersResponseSchema = z.object({ orders: z.array(marketOrderSchema) });

export const placeOrderRequestSchema = z.object({
  actionId: actionIdSchema,
  side: orderSideSchema,
  itemKey,
  quality: qualitySchema,
  price: coin,
  quantity: z.number().int().min(1).max(1000),
});
export type PlaceOrderRequest = z.infer<typeof placeOrderRequestSchema>;
export const placeOrderResponseSchema = actionResponse(
  z.object({ orderId: z.string(), side: orderSideSchema, itemKey: z.string(), quality: qualitySchema, price: z.number().int(), quantity: z.number().int(), escrow: z.number().int() }),
);

export const cancelOrderRequestSchema = z.object({ actionId: actionIdSchema, orderId: z.string().min(1).max(40) });
export const cancelOrderResponseSchema = actionResponse(z.object({ orderId: z.string(), returned: z.number().int() }));

// ---- Direct trades (/v1/market/offer*) ----

const tradeLine = z.object({ itemKey, quality: qualitySchema, quantity: z.number().int().min(1).max(100_000) });
export const tradeBundleSchema = z.object({ coin: z.number().int().min(0).max(100_000_000), items: z.array(tradeLine).max(6) });
export type TradeBundleView = z.infer<typeof tradeBundleSchema>;

export const tradeOfferSchema = z.object({
  id: z.string(),
  fromId: z.string(),
  fromName: z.string(),
  toId: z.string(),
  toName: z.string(),
  give: tradeBundleSchema,
  want: tradeBundleSchema,
  createdAt: z.number(),
  expiresAt: z.number(),
});
export type TradeOfferView = z.infer<typeof tradeOfferSchema>;

export const offersResponseSchema = z.object({ incoming: z.array(tradeOfferSchema), outgoing: z.array(tradeOfferSchema) });

export const proposeTradeRequestSchema = z.object({ actionId: actionIdSchema, to: z.string().min(1).max(40), give: tradeBundleSchema, want: tradeBundleSchema });
export type ProposeTradeRequest = z.infer<typeof proposeTradeRequestSchema>;
export const proposeTradeResponseSchema = actionResponse(z.object({ offerId: z.string() }));

export const answerTradeRequestSchema = z.object({ actionId: actionIdSchema, offerId: z.string().min(1).max(40) });
export const acceptTradeResponseSchema = actionResponse(z.object({ offerId: z.string(), shared: z.boolean() }));
export const declineTradeResponseSchema = z.object({ offerId: z.string() });

/** What the order-placing and cancelling Lambdas put on the FIFO queue: match this book. */
export const matchMessageSchema = z.object({ itemKey: z.string(), quality: qualitySchema });
export type MatchMessage = z.infer<typeof matchMessageSchema>;
